import {
  Box,
  Theme,
  Tooltip,
  Typography,
  IconButton,
  SvgIcon,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Column } from 'react-data-grid';
import MultiLineEllipsis from '../../../elements/MultiLineEllipsis/MultiLineEllipsis';
import ApplicationCell from '../../../elements/TableCells/ApplicationCell';
import IssueCategoryCell from '../../common/components/IssueTableRow/IssueCategoryCell';
import DateFormat from '../../../elements/Date/DateFormat';
import OwnerCell from '../../../elements/TableCells/OwnerCell';
import {
  Enforcement,
  Issue,
  IssueSortKey,
} from '../../common/types/issues-types';
import CustomLink from '../../../elements/Aggregation/elements/Link';
import PipelineIssueOptionsContainer from '../containers/PipelineIssueOptionsContainer';
import { ImBlocked } from 'react-icons/im';
import { GiCctvCamera } from 'react-icons/gi';
import { important } from 'common-types';
import { ReactComponent as ExclusionsIcon } from '../../../assets/icons/exclusions.svg';
import { RepoTypes } from 'common-types';
import IssueSeverityCell from '../../common/components/IssueTableRow/IssueSeverityCell';

export const usePipelineIssuesTable = (): Column<Issue>[] => {
  const { classes, cx } = useStyles();

  const columns: Column<Issue>[] = [
    {
      key: 'index',
      name: '#',
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      cellClass: cx(classes.textCenter, classes.textSecondary),
      headerCellClass: classes.textCenter,
      sortable: false,
      headerRenderer: () => {
        return <>#</>;
      },
      formatter: ({ row }) => {
        return <>{row.index + 1}</>;
      },
    },
    {
      key: IssueSortKey.Severity,
      name: 'Severity',
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      sortable: true,

      formatter: ({ row }) => {
        return <IssueSeverityCell severity={row.severity} />;
      },
    },
    {
      key: IssueSortKey.Category,
      name: 'Category',
      width: 120,
      minWidth: 120,
      maxWidth: 120,
      cellClass: classes.textSecondary,
      formatter: ({ row }) => {
        return <IssueCategoryCell issue={row} />;
      },
    },
    {
      key: IssueSortKey.CicdIssueStatus,
      name: 'Status',
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      sortable: true,
      formatter: ({ row: issue }) => (
        <Typography variant='body2' color='text.secondary'>
          {issue.cicdFields?.issueStatus}
        </Typography>
      ),
    },
    {
      key: IssueSortKey.IssueName,
      name: 'Name',
      sortable: true,
      formatter: ({ row }) => {
        return (
          <>
            <MultiLineEllipsis lines={2} variant='body2' color='text.primary'>
              {row.mainTitle}
            </MultiLineEllipsis>
            {row.excludedByAlert && (
              <Tooltip
                title={
                  <Typography variant='caption' color='text.primary'>
                    This issue was excluded
                  </Typography>
                }>
                <div>
                  <IconButton size='small' disabled={true}>
                    <SvgIcon fontSize='inherit'>
                      <ExclusionsIcon />
                    </SvgIcon>
                  </IconButton>
                </div>
              </Tooltip>
            )}
          </>
        );
      },
    },
    {
      key: IssueSortKey.RepoName,
      name: 'Application',
      width: 150,
      minWidth: 150,
      maxWidth: 150,
      sortable: true,
      cellClass: classes.textSecondary,
      formatter: ({ row }) => {
        return (
          <ApplicationCell
            appName={row.app.name}
            appType={row.app.type as RepoTypes}
          />
        );
      },
    },
    {
      key: IssueSortKey.JobId,
      name: 'Job Link',
      width: 100,
      minWidth: 100,
      maxWidth: 100,
      sortable: true,
      cellClass: classes.textSecondary,
      formatter: ({ row: { cicdFields } }) => {
        return cicdFields?.jobId ? (
          <CustomLink href={cicdFields?.jobUrl} value={cicdFields?.jobId} />
        ) : null;
      },
    },
    {
      key: IssueSortKey.JobTriggeredBy,
      name: 'Job triggered By',
      width: 150,
      minWidth: 150,
      maxWidth: 150,
      sortable: true,
      cellClass: classes.textSecondary,
      formatter: ({ row }) => {
        return row.cicdFields?.jobTriggeredBy ? (
          <OwnerCell owners={[row.cicdFields.jobTriggeredBy]} />
        ) : (
          <></>
        );
      },
    },
  ];

  columns.push({
    key: IssueSortKey.JobTriggeredAt,
    name: 'Pipeline Date',
    width: 100,
    minWidth: 100,
    maxWidth: 100,
    sortable: true,
    formatter: ({ row: issue }) => (
      <DateFormat date={issue.cicdFields?.jobTriggeredAt} />
    ),
  });
  columns.push({
    key: IssueSortKey.Enforcement,
    name: 'Result',
    width: 60,
    minWidth: 60,
    maxWidth: 60,
    sortable: true,
    cellClass: cx([classes.textCenter, classes.noPadding]),
    formatter: ({ row: issue }) => (
      <Tooltip
        title={
          <Typography variant='body2' color='text.primary'>
            {issue.cicdFields?.enforcement}
          </Typography>
        }>
        <div className={classes.resultIcon}>
          {issue.cicdFields?.enforcement === Enforcement.Monitor && (
            <GiCctvCamera size={20} color='#FFA500' />
          )}
          {issue.cicdFields?.enforcement === Enforcement.Block && (
            <ImBlocked color='red' />
          )}
        </div>
      </Tooltip>
    ),
  });
  columns.push({
    key: 'MenuOptions',
    name: '',
    width: 60,
    minWidth: 60,
    maxWidth: 60,
    sortable: false,
    cellClass: cx([classes.textCenter, classes.noPadding]),
    formatter: ({ row }) => {
      return (
        <Box display='flex' justifyContent='center'>
          <PipelineIssueOptionsContainer issue={row} />
        </Box>
      );
    },
  });
  return columns;
};

const useStyles = makeStyles()((theme: Theme) => ({
  textCenter: {
    justifyContent: 'center',
  },
  noPadding: {
    padding: important('0'),
  },
  textSecondary: {
    color: theme.palette.text.secondary,
  },
  resultIcon: {
    display: 'flex',
    alignItems: 'center',
  },
}));
